import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { researchApi, projectsApi, pipelineApi, formatApiError } from '../lib/api';
import { pollJob } from '../lib/pollJob';
import { firstNonEmpty } from '../lib/empty';
import { WriteOnly } from '../components/ViewerGuard';
import { HelpButton } from '../components/HelpPanel';
import { 
  Search, Loader2, ExternalLink, MapPin, Users, Zap, ChevronDown, ChevronUp,
  Plus, Building, Target, Sparkles, User, Film, AlertTriangle, Check, X,
  Leaf, PlayCircle, Calendar, Star, Globe, Link2, Shield, Package, Monitor,
  DollarSign, Award, TrendingUp, BookOpen, Clapperboard, BarChart3
} from 'lucide-react';

const PLACEHOLDER_EXAMPLES = [
  "Family offices in your sector...",
  "grants closing this year",
  "angel investors in your region",
  "fiscal sponsors and accelerators"
];

const EXAMPLE_CHIPS = [
  { icon: Building, label: "Research an organisation", query: "Family offices in your sector", description: "Full mandate, contacts, pitch hooks and BD angles" },
  { icon: User, label: "Profile an individual", query: "Investors in your region", description: "Background, relationship type, and engagement approach" },
  { icon: Globe, label: "Paste a URL", query: "https://www.example.org", description: "Paste any LinkedIn or website URL for instant intelligence" },
];

import HowDoesThisWorkPill from '../components/HowDoesThisWorkPill';
import usePageTitle from '../hooks/usePageTitle';

const LOADING_MESSAGES = [
  "Scanning public records and web sources...",
  "Cross-referencing mandate and funding signals...",
  "Identifying key contacts and decision makers...",
  "Analysing mission alignment...",
  "Checking for red flags and greenwash signals...",
  "Mapping relevant project angles...",
  "Building your intelligence report...",
  "Crafting pitch hooks in your voice...",
];

const MODE_LABELS = {
  organisation: { label: "Organisation", icon: Building, confidence: "high" },
  individual: { label: "Individual Profile", icon: User, confidence: "high" },
  film: { label: "Film Intelligence", icon: Clapperboard, confidence: "medium" },
  url: { label: "URL Detected", icon: Globe, confidence: "high" },
  category: { label: "Category Discovery", icon: Search, confidence: "high" },
};

const URL_SUBTYPE_LABELS = {
  linkedin_individual: { text: "LinkedIn Profile — Individual mode", confidence: "high" },
  linkedin_company: { text: "LinkedIn Company — Organisation mode", confidence: "high" },
  website: { text: "Website URL — auto-detecting type", confidence: "medium" },
};

const ResearchPage = () => {
  usePageTitle('Research Engine');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [projects, setProjects] = useState([]);
  const [mode, setMode] = useState('single');
  const [detectedMode, setDetectedMode] = useState(null);
  const [categoryResults, setCategoryResults] = useState(null);
  const [categorySelected, setCategorySelected] = useState({});
  const [savingCategory, setSavingCategory] = useState(false);
  const [categorySaved, setCategorySaved] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [addingToPipeline, setAddingToPipeline] = useState(false);
  const [pipelineSuccess, setPipelineSuccess] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [loadingMsgIndex, setLoadingMsgIndex] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [categorySubmode, setCategorySubmode] = useState('general');
  const debounceRef = useRef(null);

  useEffect(() => {
    projectsApi.getAll().then(res => setProjects(res.data)).catch(console.error);
  }, []);

  useEffect(() => {
    if (query) return;
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % PLACEHOLDER_EXAMPLES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [query]);

  // Rotate loading messages during research
  useEffect(() => {
    if (!loading) { setLoadingMsgIndex(0); return; }
    const interval = setInterval(() => {
      setLoadingMsgIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
    }, 9000);
    return () => clearInterval(interval);
  }, [loading]);

  // Debounced mode detection
  const detectMode = useCallback(async (q) => {
    if (!q.trim() || mode === 'category') {
      setDetectedMode(null);
      return;
    }
    try {
      const res = await researchApi.detectMode(q.trim());
      setDetectedMode(res.data);
    } catch {
      setDetectedMode(null);
    }
  }, [mode]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) { setDetectedMode(null); return; }
    debounceRef.current = setTimeout(() => detectMode(query), 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, detectMode]);

  const handleSearch = async (searchQuery) => {
    const q = searchQuery || query;
    if (!q.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    setCategoryResults(null);
    setPipelineSuccess(false);
    setShowHelp(false);
    setCategorySaved(false);

    const MAX_START_RETRIES = 2;

    try {
      if (mode === 'category') {
        // Async polling for category research
        let startRes = null;
        for (let retry = 0; retry <= MAX_START_RETRIES; retry++) {
          try {
            startRes = await researchApi.category(q.trim(), 7, categorySubmode);
            break;
          } catch (startErr) {
            if (retry < MAX_START_RETRIES && (!startErr.response || [502, 503, 504, 520].includes(startErr.response?.status))) {
              await new Promise(r => setTimeout(r, 2000 * (retry + 1)));
              continue;
            }
            throw startErr;
          }
        }
        const jobId = startRes.data?.job_id;
        if (!jobId) throw new Error('No job ID returned');

        await pollJob({
          fetchStatus: () => researchApi.categoryStatus(jobId).then(r => r.data),
          isComplete: (d) => d.status === 'complete' && d.result,
          isError: (d) => d.status === 'error' || d.status === 'expired',
          getError: (d) => d.error || 'Category research failed',
          onProgress: (d) => { if (d.progress) setLoadingMessage(d.progress); },
          onComplete: (d) => {
            setCategoryResults(d.result);
            const sel = {};
            (d.result.targets || []).forEach((_, i) => { sel[i] = true; });
            setCategorySelected(sel);
          },
          onError: (msg) => setError(msg),
          intervalMs: 2000,
          maxPolls: 120,
        });
      } else {
        // Async polling pattern: start job with retry, then poll for completion
        let startRes = null;
        for (let retry = 0; retry <= MAX_START_RETRIES; retry++) {
          try {
            startRes = await researchApi.run(q.trim());
            break;
          } catch (startErr) {
            if (retry < MAX_START_RETRIES && (!startErr.response || [502, 503, 504, 520].includes(startErr.response?.status))) {
              await new Promise(r => setTimeout(r, 2000 * (retry + 1)));
              continue;
            }
            throw startErr;
          }
        }
        const jobId = startRes.data.job_id;
        if (!jobId) throw new Error('No job ID returned');

        await pollJob({
          fetchStatus: () => researchApi.pollStatus(jobId).then(r => r.data),
          isComplete: (d) => d.status === 'complete',
          isError: (d) => d.status === 'error' || d.status === 'expired',
          getError: (d) => d.error || 'Research failed',
          onProgress: (d) => { if (d.progress) setLoadingMessage(d.progress); },
          onComplete: (d) => {
            setResult(d.result);
            setExpandedSections({
              mandate: true, video_evidence: true, sustainability: true, contacts: true,
              relevance: true, pitch: true, pitch_hooks: true, product_integration: true,
              screening: true, greenwash: false, philanthropic: true, rationale: true,
              background: true, mission_alignment: true, engagement: true, evidence: true,
              red_flag: false, film_details: true, financing: true, distribution: true,
              impact: true, awards: true, comparables: true, confidence: true,
              key_people: true,
            });
          },
          onError: (msg) => setError(msg),
          intervalMs: 3000,
          maxPolls: 200,
        });
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCategoryTargets = async () => {
    if (!categoryResults?.targets) return;
    setSavingCategory(true);
    try {
      const selected = categoryResults.targets.filter((_, i) => categorySelected[i]);
      await researchApi.saveCategory(selected);
      setCategorySaved(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSavingCategory(false);
    }
  };

  const handleChipClick = (chipQuery) => {
    setQuery(chipQuery);
    handleSearch(chipQuery);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || projectId;
  };

  const handleAddToPipeline = async () => {
    if (!result?.target?.id) return;
    setAddingToPipeline(true);
    try {
      await pipelineApi.create({
        target_id: result.target.id,
        bd_work_types: result.target.bd_work_types || ['Commission'],
        project_ids: result.target.relevant_projects || [],
        owner: 'james',
        stage: 'researched',
        temperature: 'cold',
      });
      setPipelineSuccess(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setAddingToPipeline(false);
    }
  };

  // ---- Reusable Components ----
  const RelevanceScore = ({ score }) => {
    const capped = Math.min(Number(score) || 0, 5);
    return (
      <div className="flex items-center gap-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className={`w-2 h-8 rounded-full transition-all ${i <= capped ? 'bg-[#00D4C8]' : 'bg-[#1A2F4C]'}`} />
        ))}
        <span className="text-sm text-[#00D4C8] ml-2">{capped}/5</span>
      </div>
    );
  };

  const RatingBadge = ({ rating }) => {
    if (!rating) return null;
    const normalized = String(rating).toUpperCase();
    const colors = {
      HIGH: 'bg-green-500/20 text-green-400 border-green-500/30',
      STRONG: 'bg-green-500/20 text-green-400 border-green-500/30',
      'STRONG FIT': 'bg-green-500/20 text-green-400 border-green-500/30',
      MEDIUM: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      MODERATE: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'POSSIBLE FIT': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      LOW: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      WEAK: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'NO FIT': 'bg-red-500/20 text-red-400 border-red-500/30',
      NONE: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      CLEAR: 'bg-green-500/20 text-green-400 border-green-500/30',
      FLAG: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      AVOID: 'bg-red-500/20 text-red-400 border-red-500/30',
      YES: 'bg-green-500/20 text-green-400 border-green-500/30',
      NO: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      PARTIAL: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      ACTIVE: 'bg-green-500/20 text-green-400 border-green-500/30',
      POSSIBLE: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      UNLIKELY: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    };
    return (
      <span className={`px-2 py-1 text-xs uppercase tracking-wide rounded border ${colors[normalized] || colors.NONE}`}>
        {rating}
      </span>
    );
  };

  const ConfidenceBadge = ({ level, notes }) => {
    if (!level) return null;
    const normalized = String(level).toUpperCase();
    const config = {
      HIGH: { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: Shield },
      MEDIUM: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: BarChart3 },
      LOW: { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: AlertTriangle },
    };
    const c = config[normalized] || config.MEDIUM;
    const Icon = c.icon;
    return (
      <div className="flex items-center gap-3">
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${c.color}`}>
          <Icon className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-sm font-medium uppercase tracking-wide">Data Confidence: {level}</span>
        </div>
        {notes && <span className="text-xs text-[#A3B5C7]">{notes}</span>}
      </div>
    );
  };

  const CollapsibleSection = ({ id, title, icon: Icon, children, badge, defaultOpen }) => (
    <div className="card-surface" data-testid={`section-${id}`}>
      <button
        onClick={() => toggleSection(id)}
        className="w-full p-6 flex items-center justify-between text-left"
        data-testid={`section-toggle-${id}`}
      >
        <div className="flex items-center gap-3 flex-wrap">
          {Icon && <Icon className="w-5 h-5 text-[#00D4C8]" strokeWidth={1.5} />}
          <h3 className="font-heading text-xl text-white">{title}</h3>
          {badge}
        </div>
        {expandedSections[id] ? <ChevronUp className="w-5 h-5 text-[#A3B5C7]" /> : <ChevronDown className="w-5 h-5 text-[#A3B5C7]" />}
      </button>
      <AnimatePresence>
        {expandedSections[id] && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  // Parse research_raw JSON
  const getParsedResearch = (target) => {
    if (!target?.research_raw) return null;
    try {
      const js = target.research_raw.indexOf('{');
      const je = target.research_raw.lastIndexOf('}') + 1;
      if (js !== -1 && je > js) return JSON.parse(target.research_raw.substring(js, je));
    } catch (e) {
      console.error('Failed to parse target.research_raw JSON:', e);
    }
    return null;
  };

  // ---- Render helpers for specific data types ----

  const renderPersonLink = (person) => {
    if (!person) return null;
    if (typeof person === 'string') return <span className="text-white">{person}</span>;
    return (
      <div className="flex items-center gap-2">
        <span className="text-white">{person.name}</span>
        {person.company && <span className="text-[#A3B5C7] text-sm">({person.company})</span>}
        {person.linkedin && (
          <a href={person.linkedin} target="_blank" rel="noopener noreferrer" className="text-[#00D4C8] hover:text-[#33DDD3]">
            <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
          </a>
        )}
      </div>
    );
  };

  const renderPitchHooks = (hooks) => {
    if (!hooks || !Array.isArray(hooks) || hooks.length === 0) return null;
    return (
      <div className="grid gap-3">
        {hooks.map((hook, i) => (
          <div key={i} className="p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#00D4C8]/10 text-[#00D4C8] rounded border border-[#00D4C8]/20">
                {hook.relationship_type}
              </span>
            </div>
            <p className="text-[#D4E2F0] italic">"{hook.hook}"</p>
          </div>
        ))}
      </div>
    );
  };

  const renderProductIntegration = (fit) => {
    if (!fit) return null;
    const assessment = typeof fit === 'string' ? fit : fit.assessment;
    const perProject = fit.per_project || [];
    return (
      <div className="space-y-4">
        {assessment && <p className="text-[#D4E2F0] leading-relaxed">{assessment}</p>}
        {perProject.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1A2F4C]">
                  <th className="text-left py-2 px-3 text-[#A3B5C7] font-medium uppercase tracking-wider text-xs">Project</th>
                  <th className="text-left py-2 px-3 text-[#A3B5C7] font-medium uppercase tracking-wider text-xs">Fit</th>
                  <th className="text-left py-2 px-3 text-[#A3B5C7] font-medium uppercase tracking-wider text-xs">Rationale</th>
                </tr>
              </thead>
              <tbody>
                {perProject.map((p, i) => (
                  <tr key={i} className="border-b border-[#1A2F4C]/50">
                    <td className="py-3 px-3 text-white font-medium">{p.project}</td>
                    <td className="py-3 px-3"><RatingBadge rating={p.fit} /></td>
                    <td className="py-3 px-3 text-[#D4E2F0]">{p.rationale}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ---- Film Intelligence Card ----
  const renderFilmIntelligenceCard = (parsed) => {
    if (!parsed || parsed.type !== 'film_intelligence') return null;
    return (
      <div className="space-y-4" data-testid="film-intelligence-card">
        {/* Data Confidence - prominent */}
        {parsed.data_confidence && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <CollapsibleSection id="confidence" title="Data Confidence" icon={Shield} badge={<ConfidenceBadge level={parsed.data_confidence} />}>
              {parsed.data_confidence_notes && (
                <p className="text-[#D4E2F0] leading-relaxed">{parsed.data_confidence_notes}</p>
              )}
            </CollapsibleSection>
          </motion.div>
        )}

        {/* Key People */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <CollapsibleSection id="key_people" title="Key People" icon={Users}>
            <div className="space-y-4">
              {parsed.director && (
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#A3B5C7] mb-1 block">Director</label>
                  {renderPersonLink(parsed.director)}
                </div>
              )}
              {parsed.producers?.length > 0 && (
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#A3B5C7] mb-2 block">Producers</label>
                  <div className="space-y-2">
                    {parsed.producers.map((p, i) => <div key={i}>{renderPersonLink(p)}</div>)}
                  </div>
                </div>
              )}
              {parsed.executive_producers?.length > 0 && (
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#A3B5C7] mb-2 block">Executive Producers</label>
                  <div className="space-y-2">
                    {parsed.executive_producers.map((p, i) => <div key={i}>{renderPersonLink(p)}</div>)}
                  </div>
                </div>
              )}
              {parsed.sales_agent && (
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#A3B5C7] mb-1 block">Sales Agent</label>
                  {typeof parsed.sales_agent === 'string' ? (
                    <span className="text-white">{parsed.sales_agent}</span>
                  ) : (
                    <div>
                      <span className="text-white">{parsed.sales_agent.company}</span>
                      {parsed.sales_agent.contact && <span className="text-[#A3B5C7] text-sm ml-2">({parsed.sales_agent.contact})</span>}
                      {parsed.sales_agent.territories && <span className="text-[#A3B5C7] text-sm ml-2">— {parsed.sales_agent.territories}</span>}
                    </div>
                  )}
                </div>
              )}
              {parsed.commissioning_platform && (
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#A3B5C7] mb-1 block">Commissioning Platform</label>
                  <span className="text-white">{parsed.commissioning_platform}</span>
                </div>
              )}
            </div>
          </CollapsibleSection>
        </motion.div>

        {/* Financing Structure */}
        {(parsed.financing_sources || parsed.financing_summary || parsed.budget_range) && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <CollapsibleSection id="financing" title="Financing Structure" icon={DollarSign}>
              <div className="space-y-4">
                {parsed.budget_range && (
                  <div className="inline-block px-4 py-2 rounded-lg bg-[#00D4C8]/10 border border-[#00D4C8]/20">
                    <span className="text-xs uppercase tracking-wider text-[#A3B5C7]">Budget Range: </span>
                    <span className="text-[#00D4C8] font-medium">{parsed.budget_range}</span>
                  </div>
                )}
                {parsed.financing_sources?.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[#1A2F4C]">
                          <th className="text-left py-2 px-3 text-[#A3B5C7] font-medium uppercase tracking-wider text-xs">Source Type</th>
                          <th className="text-left py-2 px-3 text-[#A3B5C7] font-medium uppercase tracking-wider text-xs">Entity</th>
                          <th className="text-left py-2 px-3 text-[#A3B5C7] font-medium uppercase tracking-wider text-xs">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsed.financing_sources.map((s, i) => (
                          <tr key={i} className="border-b border-[#1A2F4C]/50">
                            <td className="py-3 px-3 text-[#00D4C8] text-xs uppercase">{typeof s === 'string' ? s : s.type}</td>
                            <td className="py-3 px-3 text-white">{typeof s === 'string' ? '' : s.entity}</td>
                            <td className="py-3 px-3 text-[#D4E2F0]">{typeof s === 'string' ? '' : s.details}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {parsed.financing_summary && (
                  <p className="text-[#D4E2F0] leading-relaxed">{parsed.financing_summary}</p>
                )}
              </div>
            </CollapsibleSection>
          </motion.div>
        )}

        {/* Distribution & Sales */}
        {(parsed.distribution_platforms || parsed.territories || parsed.viewing_figures || parsed.festival_run) && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <CollapsibleSection id="distribution" title="Distribution & Sales" icon={TrendingUp}>
              <div className="space-y-4">
                {parsed.distribution_platforms?.length > 0 && (
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#A3B5C7] mb-2 block">Platforms</label>
                    <div className="flex flex-wrap gap-2">
                      {parsed.distribution_platforms.map((p, i) => (
                        <span key={i} className="px-3 py-1 bg-white/10 text-white rounded-lg text-sm">{p}</span>
                      ))}
                    </div>
                  </div>
                )}
                {parsed.territories?.length > 0 && (
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#A3B5C7] mb-2 block">Territories</label>
                    <div className="flex flex-wrap gap-2">
                      {parsed.territories.map((t, i) => (
                        <span key={i} className="px-2 py-0.5 text-xs bg-[#1A2F4C] text-[#D4E2F0] rounded">{t}</span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {parsed.theatrical_release && (
                    <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                      <label className="text-xs uppercase tracking-wider text-[#A3B5C7]">Theatrical</label>
                      <p className="text-white capitalize mt-1">{parsed.theatrical_release}</p>
                    </div>
                  )}
                  {parsed.viewing_figures && (
                    <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                      <label className="text-xs uppercase tracking-wider text-[#A3B5C7]">Viewing Figures</label>
                      <p className="text-white mt-1">{parsed.viewing_figures}</p>
                    </div>
                  )}
                  {parsed.festival_run && (
                    <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                      <label className="text-xs uppercase tracking-wider text-[#A3B5C7]">Festival Run</label>
                      <p className="text-white mt-1">{parsed.festival_run}</p>
                    </div>
                  )}
                </div>
              </div>
            </CollapsibleSection>
          </motion.div>
        )}

        {/* Impact Campaign */}
        {parsed.impact_campaign && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
            <CollapsibleSection id="impact" title="Impact Campaign" icon={Leaf}>
              <div className="space-y-4">
                {typeof parsed.impact_campaign === 'string' ? (
                  <p className="text-[#D4E2F0] leading-relaxed">{parsed.impact_campaign}</p>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <RatingBadge rating={parsed.impact_campaign.has_campaign ? 'YES' : 'NO'} />
                      <span className="text-[#A3B5C7] text-sm">Impact Campaign</span>
                    </div>
                    {parsed.impact_campaign.ngo_partners?.length > 0 && (
                      <div>
                        <label className="text-xs uppercase tracking-wider text-[#A3B5C7] mb-2 block">NGO Partners</label>
                        <div className="flex flex-wrap gap-2">
                          {parsed.impact_campaign.ngo_partners.map((p, i) => (
                            <span key={i} className="px-3 py-1 bg-green-500/10 text-green-400 rounded-lg text-sm border border-green-500/20">{p}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {parsed.impact_campaign.impact_producer && (
                      <div>
                        <label className="text-xs uppercase tracking-wider text-[#A3B5C7]">Impact Producer</label>
                        <p className="text-white mt-1">{parsed.impact_campaign.impact_producer}</p>
                      </div>
                    )}
                    {parsed.impact_campaign.screening_strategy && (
                      <div>
                        <label className="text-xs uppercase tracking-wider text-[#A3B5C7]">Screening Strategy</label>
                        <p className="text-[#D4E2F0] mt-1">{parsed.impact_campaign.screening_strategy}</p>
                      </div>
                    )}
                    {parsed.impact_campaign.measurable_outcomes && (
                      <div>
                        <label className="text-xs uppercase tracking-wider text-[#A3B5C7]">Measurable Outcomes</label>
                        <p className="text-[#D4E2F0] mt-1">{parsed.impact_campaign.measurable_outcomes}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CollapsibleSection>
          </motion.div>
        )}

        {/* Awards */}
        {parsed.awards?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <CollapsibleSection id="awards" title="Awards & Recognition" icon={Award}>
              <div className="space-y-3">
                <ul className="space-y-2">
                  {parsed.awards.map((award, i) => (
                    <li key={i} className="flex items-center gap-2 text-[#D4E2F0]">
                      <Star className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                      {award}
                    </li>
                  ))}
                </ul>
                {parsed.critical_reception && (
                  <p className="text-[#A3B5C7] italic mt-3 border-l-2 border-[#1A2F4C] pl-4">{parsed.critical_reception}</p>
                )}
              </div>
            </CollapsibleSection>
          </motion.div>
        )}

        {/* Comparability Assessment */}
        {parsed.comparable_use && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
            <CollapsibleSection id="comparables" title="Comparability" icon={BookOpen}>
              <div className="space-y-4">
                <p className="text-[#D4E2F0] italic border-l-2 border-[#00D4C8] pl-4">{parsed.comparable_use}</p>
              </div>
            </CollapsibleSection>
          </motion.div>
        )}
      </div>
    );
  };

  // ---- Organisation Sections ----
  const renderOrganisationSections = (target, parsed) => {
    const mission = firstNonEmpty(parsed?.mission_programmes, parsed?.mandate_summary, target.mandate_summary);
    const pac = firstNonEmpty(parsed?.public_activity_content, parsed?.active_video_content_evidence, parsed?.active_video_evidence);
    const pacText = typeof pac === 'string' ? pac : (pac && typeof pac === 'object' ? pac.details : null);
    const climate = firstNonEmpty(parsed?.mission_alignment, parsed?.climate_solutions_focus, parsed?.sustainability_mandate);
    const climateRating = firstNonEmpty(climate?.rating, climate?.mission_alignment_rating, climate?.ocean_specificity_rating);
    const climateDetails = typeof climate === 'string' ? climate : climate?.details;
    const frc = parsed?.funding_relationships_capacity;
    const mechanisms = firstNonEmpty(frc?.funding_mechanisms, parsed?.philanthropic_mechanism);
    const rep = firstNonEmpty(parsed?.reputational_risk_check, parsed?.greenwash_check);
    const repVerdict = rep?.verdict;
    const repAutoExpand = repVerdict === 'FLAG' || repVerdict === 'AVOID';

    return (
      <>
        {mission && (
          <CollapsibleSection id="mandate" title="Mission & Programmes" icon={Target}>
            <p className="text-[#D4E2F0] leading-relaxed whitespace-pre-wrap">{mission}</p>
          </CollapsibleSection>
        )}

        {pacText && (
          <CollapsibleSection id="video_evidence" title="Public Activity & Content" icon={PlayCircle}>
            <p className="text-[#D4E2F0] leading-relaxed">{pacText}</p>
          </CollapsibleSection>
        )}

        {climateDetails && (
          <CollapsibleSection id="sustainability" title="Mission Alignment" icon={Leaf} badge={climateRating && <RatingBadge rating={climateRating} />}>
            <p className="text-[#D4E2F0] leading-relaxed">{climateDetails}</p>
          </CollapsibleSection>
        )}

        {(mechanisms || frc?.capacity_signals) && (
          <CollapsibleSection id="funding_capacity" title="Funding Relationships & Capacity" icon={DollarSign}>
            {mechanisms && <p className="text-[#D4E2F0] leading-relaxed mb-2">{typeof mechanisms === 'string' ? mechanisms : JSON.stringify(mechanisms)}</p>}
            {frc?.capacity_signals && <p className="text-[#A3B5C7] leading-relaxed">{typeof frc.capacity_signals === 'string' ? frc.capacity_signals : JSON.stringify(frc.capacity_signals)}</p>}
          </CollapsibleSection>
        )}

        {rep && (repAutoExpand || rep.verdict !== 'CLEAR') && (
          <CollapsibleSection id="greenwash" title="Reputational Risk Check" icon={AlertTriangle} badge={<RatingBadge rating={rep.verdict} />}>
            <p className="text-[#D4E2F0] leading-relaxed">{rep.details}</p>
          </CollapsibleSection>
        )}

        {/* Suggested Pitch Hooks (array) */}
        {parsed?.suggested_pitch_hooks?.length > 0 && (
          <CollapsibleSection id="pitch_hooks" title="Pitch Hooks by Relationship Type" icon={Zap}>
            {renderPitchHooks(parsed.suggested_pitch_hooks)}
          </CollapsibleSection>
        )}
      </>
    );
  };

  // ---- Individual Sections ----
  const renderIndividualSections = (parsed) => {
    if (!parsed) return null;
    return (
      <>
        {parsed.background && (
          <CollapsibleSection id="background" title="Professional Background" icon={User}>
            <p className="text-[#D4E2F0] leading-relaxed">{parsed.background}</p>
          </CollapsibleSection>
        )}

        {(parsed.mission_alignment_details || parsed.ocean_connection_details) && (
          <CollapsibleSection id="mission_alignment" title="Mission Alignment" icon={Leaf} badge={<RatingBadge rating={parsed.mission_alignment_rating || parsed.ocean_connection_rating} />}>
            <p className="text-[#D4E2F0] leading-relaxed">{parsed.mission_alignment_details || parsed.ocean_connection_details}</p>
            {parsed.estimated_giving_capacity && (
              <div className="mt-4 p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C] inline-block">
                <span className="text-xs uppercase tracking-wider text-[#A3B5C7]">Estimated Giving Capacity: </span>
                <span className="text-[#00D4C8] font-medium">{parsed.estimated_giving_capacity}</span>
              </div>
            )}
          </CollapsibleSection>
        )}

        {parsed.evidence_items?.length > 0 && (
          <CollapsibleSection id="evidence" title="Evidence of Engagement" icon={Check}>
            <ul className="space-y-2">
              {parsed.evidence_items.map((item, idx) => (
                <li key={idx} className="flex items-start gap-3 text-[#D4E2F0]">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00D4C8] mt-2 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </CollapsibleSection>
        )}

        {parsed.engagement_approach && (
          <CollapsibleSection id="engagement" title="Engagement Approach" icon={Zap}>
            <p className="text-[#D4E2F0] leading-relaxed italic border-l-2 border-[#00D4C8] pl-4">{parsed.engagement_approach}</p>
          </CollapsibleSection>
        )}

        {parsed.red_flag_check && (
          <CollapsibleSection id="red_flag" title="Red Flag Check" icon={AlertTriangle} badge={<RatingBadge rating={parsed.red_flag_check.verdict} />}>
            <p className="text-[#D4E2F0] leading-relaxed">{parsed.red_flag_check.details}</p>
          </CollapsibleSection>
        )}
      </>
    );
  };

  // ---- Main Result Renderer ----
  const renderResult = () => {
    if (!result || loading) return null;

    // Category-type result from single mode
    if (result.type === 'category') {
      return (
        <div className="card-surface p-6" data-testid="category-result">
          <h2 className="font-heading text-2xl text-white mb-4">{result.category}</h2>
          <div className="space-y-3">
            {result.results?.map((item, index) => (
              <div key={index} className="p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C] hover:border-[#00D4C8]/30 transition-colors cursor-pointer" onClick={() => { setQuery(item.name); handleSearch(item.name); }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">{item.name}</p>
                    <p className="text-sm text-[#A3B5C7]">{item.brief}</p>
                  </div>
                  {item.relevance_score && <span className="text-sm text-[#00D4C8]">{Math.min(item.relevance_score, 5)}/5</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (result.type !== 'target' || !result.target) return null;

    const target = result.target;
    const parsed = getParsedResearch(target);
    const isFilm = parsed?.type === 'film_intelligence';
    const isIndividual = target.type === 'individual' || parsed?.type === 'individual';

    return (
      <div className="space-y-4">
        {/* Header card */}
        <div className="card-surface card-glow p-8 animate-glow" data-testid="research-result-header">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-lg bg-[#00D4C8]/10 flex items-center justify-center">
                  {isFilm ? <Clapperboard className="w-6 h-6 text-amber-400" strokeWidth={1.5} /> :
                   isIndividual ? <User className="w-6 h-6 text-[#00D4C8]" strokeWidth={1.5} /> :
                   <Building className="w-6 h-6 text-[#00D4C8]" strokeWidth={1.5} />}
                </div>
                <div>
                  <h2 className="font-heading text-3xl text-white" data-testid="result-name">
                    {isFilm ? parsed.title : (target.name || parsed?.full_name)}
                  </h2>
                  <p className="text-[#A3B5C7]">
                    {isFilm ? (
                      <span className="flex items-center gap-2">
                        {parsed.genre_subject}
                        {parsed.year && <span className="text-[#00D4C8]">({parsed.year})</span>}
                        {parsed.status && <RatingBadge rating={parsed.status} />}
                      </span>
                    ) : isIndividual ? parsed?.current_role : target.type}
                  </p>
                  {isIndividual && parsed?.organisation && <p className="text-sm text-[#A3B5C7]">{parsed.organisation}</p>}
                  {isFilm && parsed?.runtime && <p className="text-sm text-[#A3B5C7]">{parsed.runtime}</p>}
                </div>
              </div>
            </div>
            <div className="text-right space-y-2">
              {target.relevance_score && (
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-[#A3B5C7] mb-2">Relevance</p>
                  <RelevanceScore score={target.relevance_score} />
                </div>
              )}
              {isFilm && parsed?.data_confidence && (
                <ConfidenceBadge level={parsed.data_confidence} />
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            {(target.hq_location || parsed?.location) && (
              <div className="flex items-center gap-2 text-[#A3B5C7]">
                <MapPin className="w-4 h-4" strokeWidth={1.5} />
                {target.hq_location || parsed?.location}
              </div>
            )}
            {(target.website || parsed?.linkedin_url) && (
              <a href={target.website || parsed?.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#00D4C8] hover:text-[#33DDD3] transition-colors">
                <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                {target.website ? 'Website' : 'LinkedIn'}
              </a>
            )}
          </div>

          {/* BD Work Types */}
          {target.bd_work_types?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {target.bd_work_types.map((type) => (
                <span key={type} className="px-3 py-1 text-xs uppercase tracking-[0.1em] bg-[#00D4C8]/10 text-[#00D4C8] rounded-full border border-[#00D4C8]/20">{type}</span>
              ))}
            </div>
          )}

          {/* Individual relationship types */}
          {isIndividual && parsed?.relationship_types?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {parsed.relationship_types.map((type) => (
                <span key={type} className="px-3 py-1 text-xs uppercase tracking-[0.1em] bg-white/10 text-white rounded-full border border-white/20">{type}</span>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 mt-6 pt-6 border-t border-[#1A2F4C]">
            <button onClick={handleAddToPipeline} disabled={addingToPipeline || pipelineSuccess} className="btn-primary flex items-center gap-2" data-testid="add-to-pipeline-btn">
              {addingToPipeline ? <Loader2 className="w-4 h-4 animate-spin" /> : pipelineSuccess ? <><Check className="w-4 h-4" /> Added to Pipeline</> : <><Plus className="w-4 h-4" strokeWidth={2} /> Add to Pipeline</>}
            </button>
          </div>
        </div>

        {/* Type-specific sections */}
        {isFilm && renderFilmIntelligenceCard(parsed)}
        {!isFilm && isIndividual && renderIndividualSections(parsed)}
        {!isFilm && !isIndividual && renderOrganisationSections(target, parsed)}

        {/* Shared sections */}
        {target.key_contacts?.length > 0 && (
          <CollapsibleSection id="contacts" title="Key Contacts" icon={Users}>
            <div className="space-y-3">
              {target.key_contacts.map((contact, index) => (
                <div key={index} className="p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C]" data-testid={`contact-${index}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-white font-medium">{contact.name}</p>
                      <p className="text-sm text-[#00D4C8]">{contact.title || contact.role}</p>
                      {contact.notes && <p className="text-sm text-[#A3B5C7] mt-2">{contact.notes}</p>}
                      {contact.owner && <span className="text-[10px] uppercase tracking-wider text-[#A3B5C7] mt-1 block">Owner: {contact.owner}</span>}
                    </div>
                    {(contact.linkedin || contact.linkedin_url) && (
                      <a href={contact.linkedin || contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[#00D4C8] hover:text-[#33DDD3]">
                        <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Single pitch hook (fallback) */}
        {target.suggested_pitch_hook && !parsed?.suggested_pitch_hooks?.length && (
          <CollapsibleSection id="pitch" title="Suggested Pitch Hook" icon={Zap}>
            <p className="text-[#D4E2F0] leading-relaxed italic border-l-2 border-[#00D4C8] pl-4">"{target.suggested_pitch_hook}"</p>
          </CollapsibleSection>
        )}

        {/* Relevance Rationale */}
        {(result.relevance_rationale || parsed?.relevance_rationale) && (
          <CollapsibleSection id="rationale" title="Relevance Rationale" icon={Target}>
            <p className="text-[#D4E2F0] leading-relaxed">{result.relevance_rationale || parsed?.relevance_rationale}</p>
          </CollapsibleSection>
        )}

        {/* Relevant Projects */}
        {target.relevant_projects?.length > 0 && (
          <CollapsibleSection id="relevance" title="Relevant Projects" icon={Star}>
            <div className="flex flex-wrap gap-2">
              {target.relevant_projects.map((projectId) => (
                <span key={projectId} className="px-4 py-2 bg-white/10 text-white rounded-lg text-sm">{getProjectName(projectId)}</span>
              ))}
            </div>
          </CollapsibleSection>
        )}
      </div>
    );
  };

  // ---- Detected Mode Indicator ----
  // ---- Detected Mode Indicator with confidence ----
  const renderModeIndicator = () => {
    if (!detectedMode || mode === 'category' || loading) return null;
    const modeInfo = MODE_LABELS[detectedMode.mode];
    if (!modeInfo) return null;
    const ModeIcon = modeInfo.icon;
    const subtypeInfo = detectedMode.subtype ? URL_SUBTYPE_LABELS[detectedMode.subtype] : null;
    
    // Determine confidence: URL subtypes have their own, otherwise use mode-level
    const confidence = subtypeInfo?.confidence || modeInfo.confidence || 'medium';
    const isHighConfidence = confidence === 'high';
    
    const badgeColor = isHighConfidence
      ? 'text-[#00D4C8] bg-[#00D4C8]/10 border-[#00D4C8]/30'
      : 'text-amber-400 bg-amber-500/10 border-amber-500/30';
    
    const displayText = subtypeInfo?.text || `Likely: ${modeInfo.label}`;

    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="mt-3 flex items-center justify-center gap-3"
        data-testid="detected-mode-badge"
      >
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm ${badgeColor}`}>
          <ModeIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span>{displayText}</span>
        </div>
        {!isHighConfidence && (
          <button
            type="button"
            onClick={() => {
              // Toggle to category mode as an alternative
              setMode(mode === 'single' ? 'category' : 'single');
              setDetectedMode(null);
            }}
            className="text-xs text-amber-400/80 hover:text-amber-400 underline underline-offset-2 transition-colors"
            data-testid="switch-mode-link"
          >
            Not right? Switch mode
          </button>
        )}
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen">
      {/* Hero search section */}
      <div className="relative py-20 px-8 border-b border-[#1A2F4C]">
        <div className="absolute inset-0 opacity-[0.20]" style={{ backgroundImage: `url('https://images.pexels.com/photos/30204590/pexels-photo-30204590.jpeg')`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628]/50 to-[#0A1628]" />

        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00D4C8]/10 border border-[#00D4C8]/20 mb-6">
              <Sparkles className="w-4 h-4 text-[#00D4C8]" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-[0.2em] text-[#00D4C8]">AI Research Engine</span>
            </div>

            <h1 className="font-heading text-4xl md:text-5xl text-white mb-2" data-testid="research-title">Surface Intelligence</h1>
            <p className="text-sm text-[#A3B5C7]/70 mb-8 max-w-xl mx-auto">
              Research any target — paste a name, URL, or LinkedIn profile to get a full intelligence report in seconds.
            </p>

            {/* Mode toggle */}
            <div className="flex items-stretch justify-center gap-3 mb-6 max-w-2xl mx-auto" data-testid="research-mode-toggle">
              <button onClick={() => { setMode('single'); setCategoryResults(null); setDetectedMode(null); }} className={`flex-1 text-left px-4 py-3 rounded-lg border transition-colors ${mode === 'single' ? 'bg-[#00D4C8]/10 border-[#00D4C8]' : 'bg-[#0A1628] border-[#1A2F4C] hover:border-[#00D4C8]/30'}`}>
                <p className={`text-sm font-medium ${mode === 'single' ? 'text-[#00D4C8]' : 'text-[#A3B5C7]'}`}>Single Target</p>
                <p className="text-[10px] text-[#A3B5C7]/70 mt-1 leading-snug">Research any organisation, person, or URL — full intelligence report with contacts, mandate, pitch hooks, and BD angles in 1-2 minutes.</p>
              </button>
              <button onClick={() => { setMode('category'); setResult(null); setDetectedMode(null); }} className={`flex-1 text-left px-4 py-3 rounded-lg border transition-colors ${mode === 'category' ? 'bg-[#00D4C8]/10 border-[#00D4C8]' : 'bg-[#0A1628] border-[#1A2F4C] hover:border-[#00D4C8]/30'}`}>
                <p className={`text-sm font-medium ${mode === 'category' ? 'text-[#00D4C8]' : 'text-[#A3B5C7]'}`}>Category Discovery</p>
                <p className="text-[10px] text-[#A3B5C7]/70 mt-1 leading-snug">Find multiple new targets at once — describe what you're looking for and BREACH returns a ranked list of relevant organisations to research and add to your pipeline.</p>
              </button>
            </div>

            {/* Category Discovery Mode Chips */}
            <AnimatePresence>
              {mode === 'category' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-4 overflow-hidden"
                >
                  <div className="flex flex-wrap justify-center gap-2" data-testid="category-submode-chips">
                    {[
                      { key: 'film_series', label: 'Film & Series Intel', icon: Clapperboard },
                      { key: 'grant_fund', label: 'Grant & Fund Discovery', icon: DollarSign },
                      { key: 'festival_market', label: 'Festival & Market Intel', icon: Calendar },
                      { key: 'press', label: 'Press Profiling', icon: BookOpen },
                      { key: 'location_copro', label: 'Location & Co-Pro', icon: MapPin },
                      { key: 'event_speaking', label: 'Event & Speaking', icon: Users },
                      { key: 'general', label: 'General Discovery', icon: Search },
                    ].map(sm => (
                      <button
                        key={sm.key}
                        onClick={() => setCategorySubmode(sm.key)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border transition-colors ${categorySubmode === sm.key ? 'bg-[#00D4C8]/10 border-[#00D4C8] text-[#00D4C8]' : 'bg-[#0A1628] border-[#1A2F4C] text-[#A3B5C7] hover:border-[#00D4C8]/30 hover:text-white'}`}
                        data-testid={`category-submode-${sm.key}`}
                      >
                        <sm.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {sm.label}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <form onSubmit={(e) => { e.preventDefault(); handleSearch(); }} className="relative max-w-2xl mx-auto">
              {mode === 'single' && (
                <p className="text-xs text-[#A3B5C7]/60 text-center mb-2">
                  Type an organisation name, person, or paste any URL or LinkedIn profile
                </p>
              )}
              <div className="relative">
                {detectedMode?.mode === 'url' ? (
                  <Link2 className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400" strokeWidth={1.5} />
                ) : (
                  <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A3B5C7]" strokeWidth={1.5} />
                )}
                <input
                  type="text"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setShowHelp(false); }}
                  onFocus={() => !query && setShowHelp(true)}
                  placeholder={mode === 'category' ? 'e.g. UK climate tech investors and accelerators' : PLACEHOLDER_EXAMPLES[placeholderIndex]}
                  className="w-full pl-14 pr-32 py-5 rounded-xl bg-[#0E1C33] border border-[#1A2F4C] text-[#D4E2F0] text-lg placeholder:text-[#A3B5C7]/50 focus:border-[#00D4C8] focus:ring-2 focus:ring-[#00D4C8]/20 transition-colors"
                  data-testid="research-input"
                />
                <button type="submit" disabled={loading || !query.trim()} className="absolute right-3 top-1/2 -translate-y-1/2 btn-primary flex items-center gap-2" data-testid="research-submit-btn">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{mode === 'category' ? 'Discover' : 'Research'} <Target className="w-4 h-4" strokeWidth={2} /></>}
                </button>
              </div>
            </form>

            {/* Detected mode indicator */}
            <AnimatePresence>{renderModeIndicator()}</AnimatePresence>

            {/* Example chips */}
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              {EXAMPLE_CHIPS.map((chip, index) => (
                <div key={index} className="relative group">
                  <button onClick={() => handleChipClick(chip.query)} className="flex items-center gap-2 px-4 py-2 text-sm text-[#A3B5C7] bg-[#0E1C33] border border-[#1A2F4C] rounded-full hover:border-[#00D4C8]/30 hover:text-white transition-colors">
                    <chip.icon className="w-4 h-4" strokeWidth={1.5} />
                    {chip.label}
                  </button>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 rounded-lg bg-[#0A1628] border border-[#1A2F4C] text-[10px] text-[#A3B5C7] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-xl z-10">
                    {chip.description}
                  </div>
                </div>
              ))}
            </div>

            {/* Help button — centred below mode buttons */}
            <div className="flex justify-center mt-5">
              <HelpButton />
              <HowDoesThisWorkPill feature="research" />
            </div>

            {/* Collapsible help */}
            <AnimatePresence>
              {showHelp && !query && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mt-6 text-left max-w-2xl mx-auto">
                  <div className="p-6 rounded-xl bg-[#0E1C33]/80 border border-[#1A2F4C]">
                    <h4 className="text-sm font-medium text-[#00D4C8] mb-4 uppercase tracking-wider">What can I research?</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-white font-medium mb-1">Organisations</p>
                        <p className="text-[#A3B5C7]">Streamers, broadcasters, brands, foundations, distributors</p>
                      </div>
                      <div>
                        <p className="text-white font-medium mb-1">Individuals</p>
                        <p className="text-[#A3B5C7]">Investors, donors, hosts, executive producers, advisors</p>
                      </div>
                      <div>
                        <p className="text-white font-medium mb-1">Films & Series</p>
                        <p className="text-[#A3B5C7]">Financing, distribution, impact campaigns, comparables</p>
                      </div>
                      <div>
                        <p className="text-white font-medium mb-1">URLs</p>
                        <p className="text-[#A3B5C7]">Paste any LinkedIn profile, company page, or website</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>

      {/* Results section */}
      <div className="max-w-4xl mx-auto">
        {error && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 mb-6" data-testid="research-error">
            <p className="text-red-400 mb-3">{error}</p>
            <button
              onClick={() => { setError(''); handleSearch(); }}
              className="px-4 py-2 rounded-lg bg-[#00D4C8]/10 text-[#00D4C8] text-sm hover:bg-[#00D4C8]/20 transition-colors border border-[#00D4C8]/20"
              data-testid="research-retry-btn"
            >
              Retry Research
            </button>
          </motion.div>
        )}

        {loading && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16" data-testid="research-loading">
            <div className="inline-flex flex-col items-center gap-4">
              <div className="inline-flex items-center gap-3 px-6 py-4 rounded-xl bg-[#0E1C33] border border-[#1A2F4C]">
                <Loader2 className="w-6 h-6 text-[#00D4C8] animate-spin" />
                <AnimatePresence mode="wait">
                  <motion.span
                    key={loadingMsgIndex}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3 }}
                    className="text-white"
                  >
                    {LOADING_MESSAGES[loadingMsgIndex]}
                  </motion.span>
                </AnimatePresence>
              </div>
              {detectedMode && (
                <span className="text-sm text-[#A3B5C7]">
                  Mode: {MODE_LABELS[detectedMode.mode]?.label || detectedMode.mode}
                  {detectedMode.subtype && ` (${detectedMode.subtype})`}
                </span>
              )}
              <p className="text-xs text-[#A3B5C7]/50 mt-2">Typically takes 1-2 minutes — saving you hours of manual research.</p>
            </div>
          </motion.div>
        )}

        {/* Category Discovery Results */}
        <AnimatePresence>
          {categoryResults && !loading && (
            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="max-w-4xl mx-auto mt-12 space-y-4" data-testid="category-results">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-heading text-2xl text-white">{categoryResults.targets?.length || 0} Targets Found</h2>
                <button onClick={handleSaveCategoryTargets} disabled={savingCategory || categorySaved || Object.values(categorySelected).filter(Boolean).length === 0} className="btn-primary flex items-center gap-2" data-testid="save-category-targets-btn">
                  {savingCategory ? <Loader2 className="w-4 h-4 animate-spin" /> : categorySaved ? <><Check className="w-4 h-4" /> Saved to Database</> : <><Plus className="w-4 h-4" strokeWidth={2} /> Save Selected ({Object.values(categorySelected).filter(Boolean).length})</>}
                </button>
              </div>
              {(categoryResults.targets || []).map((t, i) => (
                <div key={i} onClick={() => setCategorySelected(prev => ({ ...prev, [i]: !prev[i] }))} className={`card-surface p-6 cursor-pointer transition-all ${categorySelected[i] ? 'border-[#00D4C8]/30' : 'opacity-50'}`} data-testid={`category-target-${i}`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 mt-1 ${categorySelected[i] ? 'bg-[#00D4C8] border-[#00D4C8]' : 'border-[#1A2F4C]'}`}>
                      {categorySelected[i] && <Check className="w-4 h-4 text-[#0A1628]" strokeWidth={3} />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="font-heading text-lg text-white">{t.name}</h3>
                        <span className="text-xs px-2 py-0.5 bg-white/10 text-[#A3B5C7] rounded">{t.type}</span>
                        {t.relevance_score && <span className="text-xs text-[#00D4C8]">{Math.min(t.relevance_score, 5)}/5</span>}
                      </div>
                      {t.hq_location && <div className="flex items-center gap-1 text-sm text-[#A3B5C7] mb-2"><MapPin className="w-3 h-3" /> {t.hq_location}</div>}
                      {t.bd_work_types?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {t.bd_work_types.map((bt, j) => <span key={j} className="text-[10px] uppercase px-2 py-0.5 bg-[#00D4C8]/10 text-[#00D4C8] rounded">{bt}</span>)}
                        </div>
                      )}
                      <p className="text-sm text-[#D4E2F0] mb-2">{t.mandate_summary}</p>
                      {t.suggested_pitch_hook && <p className="text-sm text-[#00D4C8] italic">"{t.suggested_pitch_hook}"</p>}
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Single Target / Film / Individual Results */}
        <AnimatePresence>
          {result && !loading && (
            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.8, ease: 'easeOut' }} data-testid="research-result">
              {renderResult()}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ResearchPage;
